import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Sparkles, Loader2, Search, Wallet, Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import CommunityManagerPanel from '../shared/CommunityManagerPanel';
import PaymentMethodBadge from '../shared/PaymentMethodBadge';
import { API_URL } from '../../config';

const ORDER_METHOD_LABELS = { mvola: 'Mvola', orange: 'Orange Money', card: 'Carte bancaire', other: 'Autre' };

function PendingCreditOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(true);
  const [actingOn, setActingOn] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/cm/admin/cm-credit-orders?status=pending`, { headers: authHeaders() });
      const data = await res.json();
      if (data.success) setOrders(data.orders);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const validate = async (id) => {
    setActingOn(id);
    try {
      await fetch(`${API_URL}/api/cm/admin/cm-credit-orders/${id}/validate`, { method: 'POST', headers: authHeaders() });
      await load();
    } finally { setActingOn(null); }
  };

  const reject = async (id) => {
    const note = window.prompt('Raison du refus (envoyée au client dans sa messagerie) :', 'Paiement non retrouvé');
    if (note === null || !note.trim()) return;
    setActingOn(id);
    try {
      await fetch(`${API_URL}/api/cm/admin/cm-credit-orders/${id}/reject`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ note })
      });
      await load();
    } finally { setActingOn(null); }
  };

  if (loading) return null;

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
      <button onClick={() => setExpanded(v => !v)} className="w-full flex items-center justify-between text-left">
        <span className="flex items-center gap-2 text-white font-semibold text-sm">
          <Wallet className="w-4 h-4 text-emerald-400" /> Paiements CM Studio en attente
          {orders.length > 0 && (
            <span className="text-[10px] bg-amber-500/20 text-amber-300 px-2 py-0.5 rounded-full">{orders.length}</span>
          )}
        </span>
        {expanded ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
      </button>
      {expanded && (
        orders.length === 0 ? (
          <p className="text-gray-500 text-xs mt-3">Aucun paiement en attente.</p>
        ) : (
          <div className="mt-3 space-y-2">
            {orders.map(o => (
              <div key={o.id} className="flex items-center justify-between gap-3 bg-black/20 rounded-xl p-3 flex-wrap">
                <div className="min-w-0">
                  <div className="text-white text-sm font-medium flex items-center gap-2 flex-wrap">{o.page_name || o.client_id} <PaymentMethodBadge method={o.method} /></div>
                  <div className="text-gray-400 text-xs">
                    Réf. {o.payment_ref} · {new Date(o.created_at).toLocaleString('fr-FR')}
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-emerald-300 font-semibold text-sm">{o.amount_ar.toLocaleString('fr-FR')} Ar</span>
                  <button
                    onClick={() => validate(o.id)}
                    disabled={actingOn === o.id}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-green-600 hover:bg-green-500 text-white text-[11px] font-medium disabled:opacity-50"
                  >
                    {actingOn === o.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />} Valider
                  </button>
                  <button
                    onClick={() => reject(o.id)}
                    disabled={actingOn === o.id}
                    className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-500/10 text-red-300 hover:bg-red-500/20 text-[11px] font-medium disabled:opacity-50"
                  >
                    <X className="w-3 h-3" /> Rejeter
                  </button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}

// ============================================================
// ADMIN - CM Studio : Community Manager IA de chaque page
// ============================================================

const authHeaders = () => ({
  'Authorization': `Bearer ${localStorage.getItem('auth_token')}`,
  'Content-Type': 'application/json'
});

export default function CMStudio() {
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState('');
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const loadClients = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/clients`, { headers: authHeaders() });
      const data = await res.json();
      setClients(data.clients || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadClients(); }, [loadClients]);

  const linkedClients = useMemo(() => clients.filter(
    c => c.status === 'active' && c.facebook_page_id && c.page_access_token
  ), [clients]);
  const filteredClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return linkedClients;
    return linkedClients.filter(c => (c.facebook_page_name || '').toLowerCase().includes(q));
  }, [linkedClients, search]);

  return (
    <div className="space-y-6 max-w-3xl">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-white mb-1 flex items-center gap-3">
          <Sparkles className="w-8 h-8 text-purple-400" />
          CM Studio
        </h1>
        <p className="text-sm text-blue-200">
          Configurez TeckiA CM pour chaque page — publication automatique de contenu
        </p>
      </div>

      <PendingCreditOrders />

      {/* Sélecteur de page */}
      <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
        <label className="text-gray-400 text-xs block mb-2">Page Facebook (client)</label>
        {loading ? <Loader2 className="w-5 h-5 text-blue-400 animate-spin" /> : (
          <div className="space-y-2 w-full md:w-96">
            <div className="relative">
              <Search className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher une page par nom..."
                className="w-full bg-slate-900 text-white border border-white/20 rounded-xl pl-9 pr-3 py-2 text-sm placeholder-gray-500"
              />
            </div>
            <select
              value={selectedClient}
              onChange={(e) => setSelectedClient(e.target.value)}
              className="w-full bg-slate-900 text-white border border-white/20 rounded-xl px-4 py-2.5 text-sm"
            >
              <option value="">— Choisir une page ({filteredClients.length}) —</option>
              {filteredClients.map(c => (
                <option key={c.$id} value={c.$id}>{c.facebook_page_name}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {selectedClient && (
        <div className="bg-white/5 border border-white/10 rounded-2xl p-4">
          <CommunityManagerPanel key={selectedClient} clientId={selectedClient} />
        </div>
      )}
    </div>
  );
}
